import { useState } from "react";
import { X, Link2, ClipboardPaste } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useContentPaste } from "@/hooks/useContentPaste";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { z } from "zod";

const contentSchema = z
  .object({
    link: z
      .string()
      .trim()
      .url({ message: "Please enter a valid URL" })
      .max(2048, { message: "URL is too long" })
      .optional()
      .or(z.literal("")),
    textContent: z
      .string()
      .trim()
      .max(10000, { message: "Text content must be less than 10000 characters" })
      .optional()
      .or(z.literal("")),
  })
  .refine((data) => data.link || data.textContent, {
    message: "Please provide either a link or text content",
  });

interface PasteContentDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onStateChange?: (state: any) => void;
  onContentCreated?: (contentId: string, contentType?: string) => void;
  mode?: "content" | "course";
  onCourseRequestCreated?: (requestId: string) => void;
  roomId?: string;
}

export const PasteContentDialog = ({
  open = true,
  onOpenChange,
  onStateChange,
  onContentCreated,
  mode = "content",
  onCourseRequestCreated,
  roomId,
}: PasteContentDialogProps) => {
  const [link, setLink] = useState("");
  const [textContent, setTextContent] = useState("");
  const [errors, setErrors] = useState<{ link?: string; textContent?: string; form?: string }>({});
  const { toast } = useToast();
  const { submitContent, isSubmitting, isFetchingMetadata } = useContentPaste();
  const { user } = useAuth();
  const [creatingCourse, setCreatingCourse] = useState(false);

  const isProcessing = isSubmitting || isFetchingMetadata || creatingCourse;

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      const validatedData = contentSchema.parse({ link, textContent });

      // Submit to database
      const result = await submitContent({ ...validatedData, roomId });

      if (result.success) {
        // If course mode and we have a content ID, create course generation request
        if (mode === "course" && result.contentId && user && onCourseRequestCreated) {
          setCreatingCourse(true);
          try {
            const { data, error } = await supabase
              .from("course_generation_requests")
              .insert({
                user_id: user.id,
                source: "paste",
                content_id: result.contentId,
              })
              .select()
              .single();

            if (error) throw error;

            // Fire-and-forget edge function
            supabase.functions
              .invoke("generate-course", { body: { requestId: data.id } })
              .catch((err) => console.error("Course generation invoke failed:", err));

            setLink("");
            setTextContent("");
            onOpenChange?.(false);
            onCourseRequestCreated(data.id);
          } catch (err: any) {
            console.error("Failed to create course request:", err);
            toast({
              title: "Error",
              description: err?.message || "Failed to start course creation",
              variant: "destructive",
            });
          } finally {
            setCreatingCourse(false);
          }
        } else {
          setLink("");
          setTextContent("");
          onOpenChange?.(false);
          if (onContentCreated && result.contentId) {
            onContentCreated(result.contentId, result.contentType);
          } else {
            onStateChange?.({ link, textContent });
          }
        }
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { link?: string; textContent?: string; form?: string } = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as "link" | "textContent"] = err.message;
          } else {
            fieldErrors.form = err.message;
          }
        });
        setErrors(fieldErrors);
      }
    }
  };

  const handleCancel = () => {
    setLink("");
    setTextContent("");
    setErrors({});
    onOpenChange?.(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/50" onClick={handleCancel} />

      {/* Dialog */}
      <div
        className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-2xl sm:max-w-[500px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col space-y-1.5 text-left">
          <h2 className="tracking-tight text-base flex items-center gap-2 font-medium">
            <Link2 className="h-4 w-4" />
            {mode === "course" ? "Import for Course" : "YouTube, Website, Etc"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {mode === "course"
              ? "Paste a link or text to generate a course from it"
              : "Enter a YouTube Link / Playlist, Website URL, Doc, ArXiv, Etc"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 pb-4">
          <div className="space-y-2">
            <div className="relative w-full">
              <Input
                className="h-10 p-6 rounded-2xl"
                placeholder="https://youtu.be/dQw4w9WgXcQ"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                name="link"
              />
              {errors.link && <p className="text-xs text-destructive mt-1">{errors.link}</p>}
            </div>
          </div>

          <div className="relative flex items-center">
            <div className="flex-grow border-t border-primary/20"></div>
            <span className="flex-shrink mx-4 text-primary/50 text-sm font-medium">or</span>
            <div className="flex-grow border-t border-primary/20"></div>
          </div>

          <div className="space-y-2">
            <h2 className="tracking-tight text-base flex items-center gap-2 font-medium">
              <ClipboardPaste className="h-4 w-4" />
              Paste Text
            </h2>
            <p className="text-sm text-muted-foreground pb-2">Copy and paste text to add as content</p>
            <div className="space-y-2">
              <Textarea
                className="resize-none min-h-[45px] sm:min-h-[126px] max-h-[80px] px-4 py-3 rounded-2xl"
                placeholder="Paste your notes here"
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                name="textContent"
              />
              {errors.textContent && <p className="text-xs text-destructive mt-1">{errors.textContent}</p>}
            </div>
          </div>

          {errors.form && <p className="text-xs text-destructive">{errors.form}</p>}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 sm:gap-0 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="rounded-lg"
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button type="submit" className="rounded-lg" disabled={isProcessing}>
              {creatingCourse
                ? "Creating course..."
                : isFetchingMetadata
                ? "Fetching metadata..."
                : isSubmitting
                ? "Adding..."
                : mode === "course"
                ? "Create Course"
                : "Add"}
            </Button>
          </div>
        </form>

        <button
          type="button"
          className="absolute right-4 top-4 rounded-lg opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          onClick={handleCancel}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </div>
    </>
  );
};
